import { useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

// the full daily lineup — one row per draw, all entered on a single screen
const DRAWS = [
  { key: "gr11", label: "Golden Raja", sub: "11AM", cat: "Golden Raja 5D", time: "11AM", digits: 5 },
  { key: "gr12", label: "Golden Raja", sub: "12PM", cat: "Golden Raja 5D", time: "12PM", digits: 5 },
  { key: "gr5", label: "Golden Raja", sub: "5PM", cat: "Golden Raja 5D", time: "5PM", digits: 5 },
  { key: "gr7", label: "Golden Raja", sub: "7PM", cat: "Golden Raja 5D", time: "7PM", digits: 5 },
  { key: "d1", label: "Dear", sub: "1PM", cat: "Dear 1PM", time: null, digits: 5 },
  { key: "kl", label: "KL", sub: "3PM", cat: "KL 3PM", time: null, digits: 6 },
  { key: "d6", label: "Dear", sub: "6PM", cat: "Dear 6PM", time: null, digits: 5 },
  { key: "d8", label: "Dear", sub: "8PM", cat: "Dear 8PM", time: null, digits: 5 },
];

const CELL_STYLES = {
  sending: "border-[#FFD700]/60",
  processing: "animate-pulse border-[#FFD700]/60",
  done: "border-[#00E676]/60",
  failed: "border-[#E63946]/70",
};

export function QuickEntry({ allCats, resHistory, onChanged }) {
  const [qDate, setQDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [values, setValues] = useState({});
  const [cellState, setCellState] = useState({});
  const [busy, setBusy] = useState(false);

  const todayStr = new Date().toLocaleDateString("en-CA");
  const yesterdayStr = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
  const showYesterdayHint = qDate === todayStr && !resHistory.some((r) => r.date === yesterdayStr);

  const entered = useMemo(() => {
    const map = {};
    for (const d of DRAWS) {
      const hit = resHistory.find(
        (r) => r.date === qDate && r.category_name === d.cat && (r.time || null) === d.time
      );
      if (hit) map[d.key] = hit.result;
    }
    return map;
  }, [resHistory, qDate]);

  const catByName = useMemo(() => {
    const m = {};
    for (const c of allCats) m[c.name] = c;
    return m;
  }, [allCats]);

  const filledCount = DRAWS.filter((d) => !entered[d.key] && (values[d.key] || "").trim()).length;

  const submit = async () => {
    const jobs = [];
    for (const d of DRAWS) {
      if (entered[d.key]) continue;
      const v = (values[d.key] || "").trim();
      if (!v) continue;
      if (v.length !== d.digits) {
        toast.error(`${d.label} ${d.sub}: சரியாக ${d.digits} இலக்கம் வேண்டும்`);
        return;
      }
      jobs.push({ d, v });
    }
    if (!jobs.length) return;
    if (
      showYesterdayHint &&
      !window.confirm(
        `⚠️ நேற்று (${yesterdayStr}) எந்த entry-ம் இல்லை.\n\nஇவை நேற்றைய results-ஆனால்: Cancel அழுத்தி Date-ஐ ${yesterdayStr}-க்கு மாற்றுங்கள்.\nஇன்றைய results என்றால்: OK அழுத்துங்கள்.`
      )
    ) {
      return;
    }
    setBusy(true);
    const started = [];
    for (const { d, v } of jobs) {
      setCellState((s) => ({ ...s, [d.key]: "sending" }));
      try {
        const res = await api.post("/admin/results", {
          category_id: catByName[d.cat]?.id,
          result: v,
          date: qDate,
          time: d.time,
        });
        started.push({ key: d.key, id: res.data.result_id });
        setCellState((s) => ({ ...s, [d.key]: "processing" }));
      } catch (e) {
        setCellState((s) => ({ ...s, [d.key]: "failed" }));
        toast.error(`${d.label} ${d.sub}: ${e.response?.data?.detail || "அனுப்ப முடியவில்லை"}`);
      }
    }
    const pending = new Set(started.map((s) => s.key));
    const keyById = Object.fromEntries(started.map((s) => [s.id, s.key]));
    const deadline = Date.now() + 8 * 60 * 1000;
    while (pending.size && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2500));
      for (const [id, key] of Object.entries(keyById)) {
        if (!pending.has(key)) continue;
        try {
          const st = await api.get(`/admin/results/${id}/status`);
          if (st.data.status === "done") {
            pending.delete(key);
            setCellState((s) => ({ ...s, [key]: "done" }));
          } else if (st.data.status === "failed") {
            pending.delete(key);
            setCellState((s) => ({ ...s, [key]: "failed" }));
          }
        } catch {}
      }
    }
    const settled = started.filter((s) => !pending.has(s.key)).length;
    toast.success(`Quick Entry முடிந்தது — ${settled} draws update ஆயின`);
    setValues({});
    setBusy(false);
    onChanged?.();
  };

  return (
    <div data-testid="quick-entry-panel" className="mb-10 border border-[#FFD700]/20 bg-[#0A0A0A] p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700]">Daily Quick Entry</p>
          <p className="mt-1 text-xs text-[#A1A1A6]">
            ஒரே தேதி — 8 draws ஒரே திரையில். ஏற்கனவே உள்ளிட்டவை பச்சையில் காட்டும்.
          </p>
        </div>
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">Date (எல்லாவற்றுக்கும்)</p>
          <input
            data-testid="quick-entry-date"
            type="date"
            value={qDate}
            onChange={(e) => setQDate(e.target.value)}
            disabled={busy}
            className="border border-white/10 bg-black px-4 py-2 text-sm text-white outline-none focus:border-[#FFD700]/60 disabled:opacity-50 [color-scheme:dark]"
          />
        </div>
      </div>
      {showYesterdayHint && (
        <p data-testid="quick-entry-date-hint" className="mb-4 border border-[#FFD700]/30 bg-[#FFD700]/5 px-4 py-3 text-xs leading-relaxed text-[#FFD700]">
          கவனம்: நேத்து ({yesterdayStr}) எந்த entry-ம் இல்லை. நேத்தைய results-ஐ enter பண்றீங்களானா, மேலே உள்ள Date-ஐ{" "}
          {yesterdayStr}-க்கு மாற்றுங்கள்.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {DRAWS.map((d) => {
          const st = cellState[d.key];
          const done = entered[d.key];
          return (
            <div
              key={d.key}
              data-testid={`quick-entry-cell-${d.key}`}
              className={`border bg-black p-3 transition-colors ${done ? "border-[#00E676]/40" : st ? CELL_STYLES[st] : "border-white/10"}`}
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#666666]">
                {d.label} <span className="text-[#FFD700]">{d.sub}</span>
              </p>
              {done ? (
                <p data-testid={`quick-entry-entered-${d.key}`} className="mt-2 font-mono text-lg font-bold tracking-[0.2em] text-[#00E676]">
                  {done} <span className="text-[10px]">✓</span>
                </p>
              ) : (
                <input
                  data-testid={`quick-entry-input-${d.key}`}
                  value={values[d.key] || ""}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, [d.key]: e.target.value.replace(/\D/g, "").slice(0, d.digits) }))
                  }
                  inputMode="numeric"
                  placeholder={"•".repeat(d.digits)}
                  disabled={busy}
                  className="mt-1 w-full bg-transparent font-mono text-lg font-bold tracking-[0.2em] text-[#FFD700] outline-none placeholder:text-[#333333] disabled:opacity-50"
                />
              )}
              {st && !done && (
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#A1A1A6]">
                  {st === "done" ? "✓ Done" : st === "failed" ? "✗ Failed" : "Updating…"}
                </p>
              )}
            </div>
          );
        })}
      </div>
      <button
        data-testid="quick-entry-submit"
        type="button"
        onClick={submit}
        disabled={busy || !filledCount}
        className="mt-5 rounded-full bg-[#FFD700] px-8 py-3 text-xs font-bold uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
      >
        {busy ? "Updating…" : filledCount ? `${filledCount} draws — அனைத்தையும் Update` : "எண்களை நிரப்புங்கள்"}
      </button>
    </div>
  );
}
